import { Color, Spacing } from '@persona-id/design-tokens';
import { groupBy } from 'lodash/fp';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { Link, matchPath, Navigate, Route } from 'react-router-dom-v5-compat';
import { COLORS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import InquiryFlowPreview from 'dashboard/components/InquiryFlowPreview';
import { generatePages } from 'dashboard/components/InquiryFlowPreview/pages';
import LocalizedDirection from 'dashboard/components/LocalizedDirection';
import { SentryRoutes } from 'dashboard/components/SentryRoute/SentryRoute';
import ThemeEditorNav from 'dashboard/components/ThemeEditorNav';
import ThemeEditorTemplateSelector from 'dashboard/components/ThemeEditorTemplateSelector';
import ThemeSelector from 'dashboard/components/ThemeSelector';
import Tooltip from 'dashboard/components/Tooltip';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NavConfirmPrompt from 'dashboard/components-lib/NavConfirmPrompt';
import { Header, Section } from 'dashboard/layouts/IndexViewLayoutV2/IndexViewLayout';
import { Template } from 'dashboard/models/template';
import { Theme } from 'dashboard/models/theme';
import DashboardEditorAdvancedView from 'dashboard/views/DashboardEditorAdvancedView';
import DashboardEditorButtonsView from 'dashboard/views/DashboardEditorButtonsView';
import DashboardEditorColorView from 'dashboard/views/DashboardEditorColorView';
import DashboardEditorCompletionPageView from 'dashboard/views/DashboardEditorCompletionPageView';
import DashboardEditorDocumentsUploadPageView from 'dashboard/views/DashboardEditorDocumentsUploadPageView';
import DashboardEditorFailedPageView from 'dashboard/views/DashboardEditorFailedPageView';
import DashboardEditorFontView from 'dashboard/views/DashboardEditorFontView';
import DashboardEditorSelectCountryPageView from 'dashboard/views/DashboardEditorSelectCountryPageView';
import DashboardEditorStartPageView from 'dashboard/views/DashboardEditorStartPageView';
import Select, { Option } from 'design-system/components/Select';
import { Label } from 'design-system/components/Text';
import { paragraphMixins } from 'design-system/mixins/text';
import { BASE } from 'design-system/variables/spacing';
import { INQUIRY_LANGUAGES_ENGLISH } from 'lib/constants/locales';
import { UiStepType } from 'lib/interfaces/StepsConfig';

const Container = styled('div')`
  display: flex;
  /* This is inside the Verify localized direction component, but we don't want its orientation to change. */
  direction: ltr;
`;

const ConfigSection = styled('div')`
  min-width: 400px;
  width: 400px;
  height: 100vh;

  background-color: ${({ theme }) => theme.color.background.primary};
  border-left: 1px solid ${({ theme }) => theme.color.border.primary};
  overflow-y: auto;

  padding: 0px;
`;

const ConfigContainer = styled('div')`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const SectionContainer = styled('div')`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
  padding-bottom: ${Spacing[5]};
`;

const titleCSS = css`
  color: ${({ theme }) => theme.color.text.primary};
  padding-top: ${Spacing[5]};
  padding-left: ${Spacing[4]};
  padding-right: ${Spacing[4]};
`;

const Title = styled('h2')`
  ${titleCSS}
`;

const SectionTitle = styled(Label).attrs(() => ({ size: 'lg', weight: 'bold' }))`
  ${titleCSS}
`;

const StyledText = styled('span')`
  ${paragraphMixins.md}
  color: ${({ theme }) => theme.color.text.brand};
  cursor: pointer;
`;

const StyledTooltip = styled(Tooltip).attrs(() => ({ placement: 'bottom', trigger: 'hover' }))`
  max-width: 340px;
  white-space: pre-wrap;
  line-height: ${BASE * 3.5}px;
`;

// Copied from ThemeEditorTemplateSelector and ThemeSelector
const PaddingContainer = styled('div')`
  padding-top: ${Spacing[4]};
  padding-left: ${Spacing[4]};
  padding-right: ${Spacing[4]};
`;

const PreviewContainer = styled('div')`
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const defaultColorAttributes: Record<string, string> = {
  'color-modal-background': COLORS.white,
  'color-button-primary': Color.CorePurple700,
  'color-font': COLORS.gray100,
  'color-font-title': COLORS.gray100,
};

interface DashboardEditorViewProps {
  handleTemplateSelect: (templateId: string) => void;
  handleThemeSelect: (themeId: string) => void;
  handleSave: () => Promise<void>;
  handleSetLocale: (locale: string) => void;
  hasUnsavedChanges: boolean;
  currentLogoUrl?: string;
  loading: boolean;
  locale: string;
  locationPathname: string;
  productFeatureIsDisabled: boolean;
  templateLiveEdited: Template;
  templates: Template[];
  templateSelectedPristine: Template;
  themeLiveEdited: Theme;
  themes: Theme[];
  themeSelectedPristine: Theme;
}

export default (props: DashboardEditorViewProps) => {
  return <MainContent {...props} />;
};

const MainContent = (props: DashboardEditorViewProps) => {
  const [slideNumber, setSlideNumber] = useState(undefined);

  useEffect(() => {
    // Send InquiryFlowPreview to specific pages for the start and completion panels
    if (matchPath({ path: `dashboard/editor/start-page` }, props.locationPathname)) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setSlideNumber(0);
    } else if (matchPath({ path: `dashboard/editor/completion-page` }, props.locationPathname)) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setSlideNumber(-2);
    } else if (matchPath({ path: `dashboard/editor/failed-page` }, props.locationPathname)) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setSlideNumber(-1);
    } else if (
      matchPath({ path: `dashboard/editor/select-country-page` }, props.locationPathname)
    ) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setSlideNumber(1);
    } else if (
      matchPath({ path: `dashboard/editor/documents-upload-page` }, props.locationPathname)
    ) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setSlideNumber(1);
    }
  }, [props.locationPathname]);

  const settings = {
    arrows: false,
    centerMode: true,
    dots: true,
    focusOnSelect: true,
    infinite: false,
    speed: 500,
    swipeToSlide: true,
    variableWidth: true,
  };

  const editorPanelProps = {
    handleSave: props.handleSave,
    handleTemplateSelect: props.handleTemplateSelect,
    handleThemeSelect: props.handleThemeSelect,
    loading: props.loading,
    templates: props.templates,
    templateSelectedPristine: props.templateSelectedPristine,
    themes: props.themes,
    themeSelectedPristine: props.themeSelectedPristine,
    reactRouterV6: true,
  };

  if (props.productFeatureIsDisabled) {
    return (
      <Section>
        <Header title='Editor' />
        <div>
          Please <Link to='/dashboard/contact-us'>contact us</Link> to edit templates.
        </div>
      </Section>
    );
  } else if (props.loading) {
    return <LoadingIndicator fullscreen={true} />;
  } else if (props.templates == null || props.templates.length === 0) {
    return (
      <Section data-test='editor-view-no-template'>
        <Header title='Editor' />
        <div data-test='editor-view-no-template__text'>
          Please <Link to='/dashboard/contact-us'>contact us</Link> to create your first template.
        </div>
      </Section>
    );
  } else {
    return (
      <>
        <NavConfirmPrompt prevent={props.hasUnsavedChanges} />
        <Suspense fallback={null}>
          <LocalizedDirection>
            <Container>
              <ConfigSection>
                <SentryRoutes>
                  <Route
                    index
                    element={
                      <EditorNavSection
                        handleTemplateSelect={props.handleTemplateSelect}
                        handleThemeSelect={props.handleThemeSelect}
                        templates={props.templates}
                        templateSelectedPristine={props.templateSelectedPristine}
                        themes={props.themes}
                        themeSelectedPristine={props.themeSelectedPristine}
                        handleSetLocale={props.handleSetLocale}
                        locale={props.locale}
                      />
                    }
                  />

                  <Route
                    path='color'
                    element={
                      <DashboardEditorColorView
                        {...editorPanelProps}
                        defaultColorAttributes={defaultColorAttributes}
                      />
                    }
                  />

                  <Route path='font' element={<DashboardEditorFontView {...editorPanelProps} />} />

                  <Route
                    path='buttons'
                    element={<DashboardEditorButtonsView {...editorPanelProps} />}
                  />

                  <Route
                    path='advanced'
                    element={
                      <DashboardEditorAdvancedView
                        {...editorPanelProps}
                        defaultColorAttributes={defaultColorAttributes}
                      />
                    }
                  />

                  <Route
                    path='start-page'
                    element={
                      <DashboardEditorStartPageView
                        {...editorPanelProps}
                        currentLogoUrl={props.currentLogoUrl}
                      />
                    }
                  />

                  <Route
                    path='select-country-page'
                    element={<DashboardEditorSelectCountryPageView {...editorPanelProps} />}
                  />

                  <Route
                    path='documents-upload-page'
                    element={<DashboardEditorDocumentsUploadPageView {...editorPanelProps} />}
                  />

                  <Route
                    path='completion-page'
                    element={<DashboardEditorCompletionPageView {...editorPanelProps} />}
                  />

                  <Route
                    path='failed-page'
                    element={<DashboardEditorFailedPageView {...editorPanelProps} />}
                  />

                  <Route path='*' element={<Navigate to='.' replace />} />
                </SentryRoutes>
              </ConfigSection>
              <PreviewContainer>
                {props.templateSelectedPristine ? (
                  <InquiryFlowPreview
                    theme={props.themeLiveEdited}
                    templateId={props.templateLiveEdited.id}
                    pages={generatePages(props.themeLiveEdited, props.templateLiveEdited)}
                    settings={settings}
                    showProgressBar={true}
                    goToSlideNumber={slideNumber}
                  />
                ) : (
                  <TemplateNullState />
                )}
              </PreviewContainer>
            </Container>
          </LocalizedDirection>
        </Suspense>
      </>
    );
  }
};

interface EditorNavSectionProps {
  handleTemplateSelect: (templateId: string) => void;
  handleThemeSelect: (themeId: string) => void;
  templates: Template[];
  templateSelectedPristine: Template;
  themes: Theme[];
  themeSelectedPristine: Theme;
  handleSetLocale: (locale: string) => void;
  locale: string;
}

const EditorNavSection = ({
  handleTemplateSelect,
  handleThemeSelect,
  templates,
  templateSelectedPristine,
  themes,
  themeSelectedPristine,
  handleSetLocale,
  locale,
}: EditorNavSectionProps) => {
  const groupedSteps = groupBy(
    (step) => step.type,
    templateSelectedPristine?.attributes['steps-config']?.steps,
  );
  const showStartPage = groupedSteps[UiStepType.Start] != null;
  const showSelectCountryPage =
    groupedSteps[UiStepType.CountrySelect] != null &&
    templateSelectedPristine?.attributes['enabled-country-codes'].length !== 1;

  const showDocumentsUploadPage =
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    templateSelectedPristine?.attributes['documents-config']?.required?.length > 1;

  const associatedTemplates = useMemo<[countLabel: string, namesLabel: string]>(() => {
    // filter templates that use the currently selected theme as default.
    const themeTemplates = templates.filter((template) => {
      return (
        template.relationships['default-theme'] != null &&
        template.relationships['default-theme'].data.id === themeSelectedPristine.id
      );
    });

    // construct count string (singular for count of 1, plural otherwise)
    const templateCount = themeTemplates.length;
    const templateNounForm = templateCount === 1 ? 'template' : 'templates';
    const countLabel = ` ${templateCount} inquiry ${templateNounForm}`;

    // construct name string with newlines for tooltip
    const templateNames =
      templateCount === 1
        ? themeTemplates[0].attributes.name
        : themeTemplates.reduce((template, val) => {
            return `${template}${val.attributes.name}\n`;
          }, '');

    return [countLabel, templateNames];
  }, [themeSelectedPristine, templates]);

  return (
    <ConfigContainer>
      <div>
        <SectionContainer>
          <Title>Theme Editor</Title>
          <ThemeSelector
            handleThemeSelect={handleThemeSelect}
            themes={themes}
            themeSelectedPristine={themeSelectedPristine}
          />
          <PaddingContainer>
            <div data-test='editor-view-related-templates__text'>
              This theme is used by
              <StyledTooltip tooltip={associatedTemplates[1]} hideArrow={false}>
                <StyledText>{associatedTemplates[0]}</StyledText>
              </StyledTooltip>
            </div>
          </PaddingContainer>
        </SectionContainer>
        <SectionContainer>
          <SectionTitle>Gallery Preview</SectionTitle>
          <ThemeEditorTemplateSelector
            handleTemplateSelect={handleTemplateSelect}
            templates={templates}
            value={templateSelectedPristine?.id}
          />
          <PaddingContainer>
            <FormControlLabel>Language</FormControlLabel>
            <Select onValueChange={(value) => handleSetLocale(value)} value={locale}>
              {(templateSelectedPristine?.attributes['enabled-locales'].length > 0
                ? templateSelectedPristine.attributes['enabled-locales']
                : Object.keys(INQUIRY_LANGUAGES_ENGLISH)
              )
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                .map((locale: keyof typeof INQUIRY_LANGUAGES_ENGLISH) => (
                  <Option key={locale} value={locale}>
                    {INQUIRY_LANGUAGES_ENGLISH[locale]}
                  </Option>
                ))}
            </Select>
          </PaddingContainer>
        </SectionContainer>
        <SectionContainer>
          <SectionTitle>Configurations</SectionTitle>
        </SectionContainer>
        <ThemeEditorNav
          showStartPage={showStartPage}
          showSelectCountryPage={showSelectCountryPage}
          showDocumentsUploadPage={showDocumentsUploadPage}
          showTerminalPages={true}
        />
      </div>
    </ConfigContainer>
  );
};

const NullStateContainer = styled('div')`
  margin: ${Spacing[2]};
`;

const TemplateNullState = () => {
  return (
    <NullStateContainer>
      Please <Link to='/dashboard/contact-us'>contact us</Link> to create your first template.
    </NullStateContainer>
  );
};
